import ChatIcon from '@mui/icons-material/Chat';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Button, ClickAwayListener, FormControl, Grid, MenuItem, Paper, Popper, Select, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';

import { DataAPIContext, GET_LIST, UPDATE } from '../../api';
import WhiteBackgroundTooltip from '../../components/WhiteTooltip';
import { ContextStore } from '../../store/contextStore';
import { extractStartAndEndDate, formatNewUserManagerPushURL } from '../utils';

const useStyles = makeStyles((theme) => ({
    tagListContainer: {
        display: 'flex',
        overflowX: 'hidden',
        gap: '0.25rem',
    },
    tagCapsule: {
        display: 'flex', gap: '0.25rem', backgroundColor: '#F1F1F2', borderRadius: '2px', padding: '0.15rem 0.4rem', alignItems: 'center', opacity: 0.85,
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer'
    },
    tagsPaperModalContainer: {
        boxShadow: '0px 8px 10px rgba(0, 0, 0, 0.14), 0px 3px 14px rgba(0, 0, 0, 0.12), 0px 5px 5px rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        backgroundColor: 'white',
        width: '350px',
        height: '12rem',
        padding: '1rem',
        marginBottom: '1rem',
    },
    tooltipTagCapsule: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '6px 16px',
        overflow: 'hidden',
        borderRadius: '16px',
        backgroundColor: '#E0E0E0',
        color: 'rgba(0, 0, 0, 0.87)',
        flexShrink: 0,
    }
}));

export const GuestRoleUniqId = Math.floor(Math.random() * Math.floor(Math.random() * Date.now()));

export const DateCell = (props) => {
    const [userDate, setUserDate] = useState(props.user.date);

    useEffect(() => {
        setUserDate(props.user.date);
    }, [props.user.date]);

    const dataAPI = React.useContext(DataAPIContext);
    const nonBespokeStartDate = extractStartAndEndDate(props.trip.schedule)?.startDate
    const nonBespokeEndDate = extractStartAndEndDate(props.trip.schedule)?.endDate
    //Check for existing user with the same date for date picker
    const checkExistingTourUser = async (email_address, date) => {
        props.setSyncActive(true);
        let error = false;
        let params = {
            filter: {
                query: email_address,
                filters: `[{"field":"date","value":"${date}"}]`,
            },
            urlSuffix: `?tour_id=${props.trip.id}`,
        };
        await dataAPI(GET_LIST, 'tour_user', params)
            .then((res) => {
                if (res.data && res.data.length > 0 && res.data[0].date === date && res.data[0].user.email === email_address && !props.trip.is_bespoke) {
                    error = true;
                }
                return error;
            })
            .finally(props.setSyncActive(false));
        return error;
    };

    const onDateChange = (nd) => {
        let newDate = nd ? moment.utc(nd).format('yyyy-MM-DD') : null;
        const oldValue = userDate ? moment.utc(userDate).format('yyyy-MM-DD') : null;
        if (!window.confirm(`Please confirm a manual date change of the tour from ${userDate} to ${moment.utc(newDate).format('yyyy-MM-DD')}`)) {
            resetToOldValue(oldValue);
        } else {
            let userToUpdate = { ...props.user, date: nd ? moment.utc(nd).format('yyyy-MM-DD') : null };
            let resource = 'tour_user';
            if (newDate !== userDate) {
                props.setSyncActive(true);
                props.setRelatedLoading(true);

                const params = {
                    id: userToUpdate.id,
                    data: { ...userToUpdate, tour_id: props.trip.id },
                };
                checkExistingTourUser(props.user.user.email, moment.utc(nd).format('yyyy-MM-DD')).then((res) => {
                    if (!res) {
                        props
                            .dataAPI(UPDATE, resource, params)
                            .then((res) => {
                                if (res.statusCode === 200 || res.statusCode === 201) {
                                    props.enqueueSnackbar('User updated', { variant: 'success' });
                                    props.setLastUpdate(new Date().toString());
                                } else if (res.statusCode === 406 || res.statusCode === 400) {
                                    if (res.data && res.data.message) {
                                        props.enqueueSnackbar(res.data.message, { variant: 'error', autoHideDuration: 5000 });
                                    }
                                }
                            })
                            .catch((err, ...args) => console.log('update error', err, args))
                            .finally(() => {
                                props.setSyncActive(false);
                                props.setRelatedLoading(false);
                            });
                    } else {
                        props.enqueueSnackbar('This User is already registered on the same date.', { variant: 'error', autoHideDuration: 5000 });
                    }
                });
            }
        }
    };

    const resetToOldValue = (oldValue) => {
        // Workaround for DatePicker bug. DatePicker won't update the displayed value if it is reset
        // to the original value but will display the new selected value even when cancelled
        setTimeout(() => {
            setUserDate(moment.utc('1801-01-01').format('yyyy-MM-DD'));
            setUserDate(moment.utc(oldValue).format('yyyy-MM-DD'));
        }, 0);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <div style={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                <DatePicker
                    value={moment.utc(userDate) || null}
                    onChange={onDateChange}
                    disabled={props.syncActive || (props.trip.length > 0 && moment.utc(userDate).add('days', Math.ceil(props.trip.length / 86400)) < moment.utc())}
                    format={'LL'}
                    fullWidth
                    minDate={nonBespokeStartDate ? moment.utc(nonBespokeStartDate) : null}
                    maxDate={nonBespokeEndDate ? moment.utc(nonBespokeEndDate) : null}
                    required
                    slotProps={{
                        textField: {
                            variant: 'filled',
                            size: 'small',
                            hiddenLabel: true,
                            sx: {
                                '& .MuiFilledInput-root': {
                                    height: 37,
                                },
                            },
                        },
                    }}
                />
            </div>
        </LocalizationProvider>
    );
};

export const TagCell = (props) => {
    const user = props.user;
    const classes = useStyles();
    const history = useHistory();

    const [tagsPreviewAnchorEl, setTagsPreviewAnchorEl] = React.useState(null);

    const hideTagsPreview = (ev) => {
        setTagsPreviewAnchorEl(null);
    };
    return (
        <WhiteBackgroundTooltip
            disableInteractive={false}
            title={props.user.tags.map((tag, idx) => {
                return (
                    <a
                        className={classes.tagCapsule}
                        key={idx}
                        href={`/tags/${tag.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {props.trip.tag_categories.filter((tc) => tc.category.id === tag.category.id && tc.chat_conversation).length > 0 && <ChatIcon style={{ scale: '0.75', margin: 0 }} />}
                        {props.trip.tag_categories.filter((tc) => tc.category.id === tag.category.id && tc.location_sharing).length > 0 && <LocationOnIcon style={{ scale: '0.75' }} />}
                        <Typography whiteSpace="wrap" sx={{ maxWidth: "200px" }}>{tag.name}</Typography>
                    </a>
                );
            })}
        >
            <div style={{ width: '100%' }}>
                <Grid container direction="row" alignItems="center">
                    <div className={classes.tagListContainer}>
                        {props.user.tags.map((tag, idx) => {
                            return (
                                <a
                                    key={idx}
                                    className={classes.tagCapsule}
                                    href={`/tags/${tag.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {props.trip.tag_categories.filter((tc) => tc.category.id === tag.category.id && tc.chat_conversation).length > 0 && <ChatIcon style={{ scale: '0.75', margin: 0 }} />}
                                    {props.trip.tag_categories.filter((tc) => tc.category.id === tag.category.id && tc.location_sharing).length > 0 && <LocationOnIcon style={{ scale: '0.75' }} />}
                                    {tag.name}
                                </a>
                            );
                        })}
                    </div>
                </Grid>
                <Popper
                    open={Boolean(tagsPreviewAnchorEl)}
                    style={{ zIndex: 1 }}
                    anchorEl={tagsPreviewAnchorEl}
                    onClose={hideTagsPreview}
                    placement="bottom-start"
                    disablePortal={false}
                // modifiers={{
                //     flip: {
                //         enabled: true,
                //     },
                //     preventOverflow: {
                //         enabled: true,
                //         boundariesElement: 'scrollParent',
                //     },
                // }}
                >
                    <ClickAwayListener onClickAway={hideTagsPreview}>
                        <Paper>
                            <div className={classes.tagsPaperModalContainer}>
                                <div style={{ height: '8.5rem', overflowY: 'scroll' }}>
                                    {props.user.tags.map((tag) => {
                                        return (
                                            <a
                                                key={tag.id}
                                                className={classes.tagCapsule}
                                                style={{ maxWidth: '300px', display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem', height: '1.5rem' }}
                                                href={`/tags/${tag.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {props.trip.tag_categories.filter((tc) => tc.category.id === tag.category.id && tc.chat_conversation).length > 0 && <ChatIcon />}
                                                {props.trip.tag_categories.filter((tc) => tc.category.id === tag.category.id && tc.location_sharing).length > 0 && <LocationOnIcon />}
                                                <Typography>{tag.name}</Typography>
                                            </a>
                                        );
                                    })}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: 'white', height: '65px', alignItems: 'center' }}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        onClick={(ev) => {
                                            props.setTourUserId(null);
                                            history.push(formatNewUserManagerPushURL(props.searchFilterList, props.recordListFilters, props.trip.id, user.id));
                                            //Workaround for updating the tripUser preview's local state
                                            setTimeout(() => {
                                                props.setTourUserId(user.id);
                                            }, 300);
                                            hideTagsPreview(ev);
                                        }}
                                    >
                                        {' '}
                                        Edit{' '}
                                    </Button>{' '}
                                    <Button color="primary" variant="outlined" onClick={hideTagsPreview}>
                                        {' '}
                                        Cancel{' '}
                                    </Button>
                                </div>
                            </div>
                        </Paper>
                    </ClickAwayListener>
                </Popper>
            </div>
        </WhiteBackgroundTooltip>
    );
};

export const RolesCell = (props) => {
    const { roleMap } = React.useContext(ContextStore);

    const guestRole = {
        id: GuestRoleUniqId,
        name: 'Guest',
    };
    const user = {
        ...props.user,
        role: props?.user?.role ? props.user.role : guestRole,
    };

    let allowedRoles = [];

    //Add the current user role to allowed roles
    if (props.user.role && props.user.role.id !== GuestRoleUniqId) {
        allowedRoles = [props.roleMap[props.user.role.id]];
    }

    if (user.user?.is_tour_operator_staff || user.user?.roles?.length > 0) {
        props.user?.user.roles.forEach((r) => {
            if ((props.user.role && props.user.role?.id !== r) || !props.user.role) {
                return allowedRoles.push(props.roleMap[r]);
            }
        });
    } else {
        allowedRoles = [...allowedRoles, guestRole];
    }

    const onRoleChange = (e) => {
        e.stopPropagation();
        let roleId = e.target.value;

        const roleMapWithGuest = {
            ...props.roleMap,
            GuestRoleUniqId: { id: GuestRoleUniqId, name: 'Guest' },
        };
        let userToUpdate;
        if (roleId === GuestRoleUniqId) {
            userToUpdate = {
                ...user,
                role: null,
                _changed: true,
            };
        } else {
            userToUpdate = {
                ...user,
                role: roleMapWithGuest[+roleId],
                _changed: true,
            };
        }

        let resource = 'tour_user';
        props.setSyncActive(true);

        const params = {
            id: userToUpdate.id,
            data: { ...userToUpdate, tour_id: props.trip.id },
        };
        props
            .dataAPI(UPDATE, resource, params)
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201) {
                    props.enqueueSnackbar('User updated', { variant: 'success' });
                    props.setLastUpdate(new Date().toString());
                } else if (res.statusCode === 406 || res.statusCode === 400) {
                    if (res.data && res.data.message) {
                        props.enqueueSnackbar(res.data.message, { variant: 'error', autoHideDuration: 5000 });
                    }
                }
            })
            .catch((err, ...args) => console.log('update error', err, args))
            .finally(() => {
                props.setSyncActive(false);
            });
    };

    return (
        <FormControl fullWidth>
            <Select
                value={user?.role?.id || GuestRoleUniqId}
                renderValue={(v, test) => {
                    return <p style={{ textTransform: 'capitalize' }}>{roleMap[v] ? roleMap[v]?.name : 'Guest'}</p>;
                }}
                variant="filled"
                onChange={onRoleChange}
                style={{ height: '37px' }}
                disabled={allowedRoles.length === 1}
            >
                {allowedRoles.map((r) => {
                    return (
                        <MenuItem key={r.id} value={r.id} style={{ textTransform: 'capitalize' }}>
                            {r.name}
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
};
